/**
 * Tile packs and deck building. [P1-tiles] [P1-tiles-expansion-shape]
 *
 * "The base game is just another pack" ([D-11]) — the deck builder takes a list of pack IDs and there
 * is no base-vs-expansion branch anywhere in the engine. Adding an expansion later means authoring a
 * pack and passing its ID; it does not mean touching this file's logic.
 */
import { SUBEDGE_TERRAIN } from '../types.js';
import { BASE_TILES, START_TILE_ID } from './base.js';
export { BASE_TILES, START_TILE_ID };
export const PACKS = {
    base: BASE_TILES,
};
export function buildTileIndex(packIds = ['base']) {
    const index = new Map();
    for (const packId of packIds) {
        const pack = PACKS[packId];
        if (!pack)
            throw new Error(`Unknown tile pack: ${packId}`);
        for (const def of pack) {
            if (index.has(def.id))
                throw new Error(`Duplicate tile id across packs: ${def.id}`);
            index.set(def.id, def);
        }
    }
    return index;
}
/**
 * The full draw pile, as tile-def IDs, excluding the one copy of the start tile already on the board.
 * Unshuffled — the caller shuffles with the seeded RNG so that the deck is a function of the seed.
 */
export function buildDeck(packIds = ['base']) {
    const deck = [];
    let startRemoved = false;
    for (const packId of packIds) {
        const pack = PACKS[packId];
        if (!pack)
            throw new Error(`Unknown tile pack: ${packId}`);
        for (const def of pack) {
            let copies = def.copies;
            if (def.isStart && !startRemoved) {
                copies -= 1;
                startRemoved = true;
            }
            for (let i = 0; i < copies; i++)
                deck.push(def.id);
        }
    }
    return deck;
}
/** Total tiles in a pack set, including the start tile. */
export function totalTiles(packIds = ['base']) {
    return packIds.reduce((sum, id) => sum + (PACKS[id] ?? []).reduce((s, def) => s + def.copies, 0), 0);
}
/**
 * Structural validation of a tile pack.
 *
 * Every one of the twelve sub-edges must be owned by exactly one feature, and that feature's kind must
 * agree with the terrain the tile's `edges` imply for that sub-edge. This catches the whole class of
 * authoring slips that would otherwise surface much later as a farm that mysteriously fails to merge —
 * the single hardest category of bug in a Carcassonne implementation to diagnose from a symptom.
 */
export function validateTilePack(defs) {
    const problems = [];
    for (const def of defs) {
        const owner = new Map();
        for (let fi = 0; fi < def.features.length; fi++) {
            const feature = def.features[fi];
            if (!feature)
                continue;
            if (feature.kind === 'cloister') {
                if (feature.subEdges.length > 0) {
                    problems.push(`${def.id}: cloister feature ${fi} must own no sub-edges`);
                }
                continue;
            }
            for (const subEdge of feature.subEdges) {
                if (subEdge < 0 || subEdge > 11) {
                    problems.push(`${def.id}: feature ${fi} has out-of-range sub-edge ${subEdge}`);
                    continue;
                }
                const existing = owner.get(subEdge);
                if (existing !== undefined) {
                    problems.push(`${def.id}: sub-edge ${subEdge} is claimed by features ${existing} and ${fi}`);
                    continue;
                }
                owner.set(subEdge, fi);
                const side = Math.floor(subEdge / 3);
                const offset = subEdge % 3;
                const sideTerrain = def.edges[side];
                if (!sideTerrain)
                    continue;
                const expected = SUBEDGE_TERRAIN[sideTerrain][offset];
                if (expected !== feature.kind) {
                    problems.push(`${def.id}: sub-edge ${subEdge} is ${expected} by its side terrain (${sideTerrain}) ` +
                        `but belongs to a ${feature.kind} feature`);
                }
            }
            if (feature.kind === 'field') {
                for (const cityIndex of feature.adjacentCities ?? []) {
                    if (def.features[cityIndex]?.kind !== 'city') {
                        problems.push(`${def.id}: field feature ${fi} lists adjacentCities[${cityIndex}], which is not a city`);
                    }
                }
            }
            if (feature.kind !== 'field' && feature.adjacentCities !== undefined) {
                problems.push(`${def.id}: adjacentCities is meaningful only on fields (feature ${fi})`);
            }
            if (feature.kind !== 'city' && feature.pennant) {
                problems.push(`${def.id}: only cities may carry a pennant (feature ${fi})`);
            }
        }
        for (let subEdge = 0; subEdge < 12; subEdge++) {
            if (!owner.has(subEdge)) {
                problems.push(`${def.id}: sub-edge ${subEdge} is not owned by any feature`);
            }
        }
    }
    return problems;
}
//# sourceMappingURL=index.js.map